import React, { useEffect, useRef, useState } from 'react';

const GrantitudeSpiral = ({ size = 300, animate = true, speed = 0.9 }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const centerX = size / 2;
    const centerY = size / 2;
    
    let animationTime = 0;
    const baseAlpha = 1.618; // Golden ratio - the heart of Grant math

    const drawGrantitudeSpiral = (time = 0) => {
      // Clear canvas completely (transparent) instead of filling with black
      ctx.clearRect(0, 0, size, size);

      // Enhanced 3D spiral layers with more vibrant colors
      const layers = [
        { phase: 0, color: [255, 215, 0], thickness: 5, zDepth: 1 },           // Bright gold
        { phase: Math.PI / 2, color: [255, 140, 0], thickness: 4.5, zDepth: 0.8 },  // Orange
        { phase: Math.PI, color: [255, 69, 0], thickness: 4, zDepth: 0.6 },     // Red-orange
        { phase: 3 * Math.PI / 2, color: [255, 20, 147], thickness: 3.5, zDepth: 0.4 }, // Deep pink
        { phase: Math.PI / 4, color: [138, 43, 226], thickness: 3, zDepth: 0.2 },   // Blue violet
      ];

      layers.forEach((layer, layerIndex) => {
        ctx.beginPath();
        const intensity = 0.95 + Math.sin(time * 0.02 + layerIndex) * 0.3;
        const zOffset = layer.zDepth * Math.sin(time * 0.01) * 25;
        
        ctx.strokeStyle = `rgba(${layer.color.join(',')}, ${intensity})`;
        ctx.lineWidth = layer.thickness * (1 + Math.sin(time * 0.015 + layerIndex) * 0.4);
        ctx.lineCap = 'round';
        
        // Enhanced mystical glow effect - brighter and more colorful
        ctx.shadowColor = `rgba(${layer.color.join(',')}, 0.9)`;
        ctx.shadowBlur = 20 + Math.sin(time * 0.03 + layerIndex) * 12;

        let firstPoint = true;
        
        // THE CORE GRANT FORMULA: R = α⋅π/(2⋅(−θ+3π))
        for (let theta = 0; theta <= 8 * Math.PI; theta += 0.025) {
          const denominator = 2 * (-theta + 3 * Math.PI + layer.phase);
          const r = denominator !== 0 ? (baseAlpha * Math.PI) / denominator : 0;
          
          // Enhanced 3D spherical coordinates with consciousness depth
          const phi = theta * 0.6 + time * 0.009 + layer.phase;
          const sphericalR = Math.abs(r) * 0.9 + 18 + zOffset;
          
          // Project 3D to 2D with enhanced perspective - the cosmic view
          const z = Math.sin(phi) * sphericalR * 0.6;
          const perspective = 200 / (200 + z);
          
          const x = centerX + (sphericalR * Math.cos(theta + time * 0.007 + layer.phase)) * perspective;
          const y = centerY + (sphericalR * Math.sin(theta + time * 0.007 + layer.phase) * Math.cos(phi)) * perspective;

          if (firstPoint) {
            ctx.moveTo(x, y);
            firstPoint = false;
          } else {
            ctx.lineTo(x, y);
          }
        }
        
        ctx.stroke();
        ctx.shadowBlur = 0;
      });

      // Enhanced central consciousness core - brighter and more vibrant
      const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 90);
      const coreIntensity = 0.6 + Math.sin(time * 0.04) * 0.4;
      coreGradient.addColorStop(0, `rgba(255, 255, 255, ${coreIntensity})`);
      coreGradient.addColorStop(0.2, `rgba(255, 215, 0, ${coreIntensity * 0.9})`);
      coreGradient.addColorStop(0.5, `rgba(255, 140, 0, ${coreIntensity * 0.7})`);
      coreGradient.addColorStop(0.8, `rgba(255, 69, 0, ${coreIntensity * 0.4})`);
      coreGradient.addColorStop(1, 'rgba(138, 43, 226, 0)');
      
      ctx.fillStyle = coreGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 90, 0, 2 * Math.PI);
      ctx.fill();
    };

    const animate_spiral = () => {
      drawGrantitudeSpiral(animationTime);
      animationTime += speed; // Configurable speed for animation
      
      if (animate) {
        animationRef.current = requestAnimationFrame(animate_spiral);
      }
    };

    if (animate) {
      animate_spiral();
    } else {
      drawGrantitudeSpiral();
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [size, animate, speed]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'transparent',
        mixBlendMode: 'screen',
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      }}
    />
  );
};

export default GrantitudeSpiral;